/**
 * Encompass Itemization Fees Types
 *
 * Types for the Encompass fee management system, aligned with
 * the V3 /settings/fees/itemization API.
 */

/**
 * LE Section codes from Encompass
 * - Section800: Origination Charges (Page 2, Section A)
 * - Section900: Services You Cannot Shop For / Prepaids (Page 2, Section B/F)
 * - Section1000: Initial Escrow Payment at Closing (Page 2, Section G)
 * - Section1100: Services You Can Shop For / Title Charges (Page 2, Section C/D)
 * - Section1200: Taxes and Other Government Fees (Page 2, Section E)
 * - Section1300: Other (Page 2, Section H)
 */
export type LESection =
  | 'Section800'
  | 'Section900'
  | 'Section1000'
  | 'Section1100'
  | 'Section1200'
  | 'Section1300';

/**
 * LE Section display metadata
 */
export const LE_SECTION_LABELS: Record<LESection, { label: string; description: string }> = {
  Section800: { label: 'Section 800', description: 'Origination Charges' },
  Section900: { label: 'Section 900', description: 'Services You Cannot Shop For / Prepaids' },
  Section1000: { label: 'Section 1000', description: 'Initial Escrow Payment at Closing' },
  Section1100: { label: 'Section 1100', description: 'Services You Can Shop For / Title Charges' },
  Section1200: { label: 'Section 1200', description: 'Taxes and Other Government Fees' },
  Section1300: { label: 'Section 1300', description: 'Other' }
};

/**
 * Calculation type for fee amounts
 */
export type FeeCalculationType = 'fixed' | 'percentage' | 'per_thousand';

/**
 * Calculation base for percentage/per_thousand fees
 */
export type FeeCalculationBase = 'loan_amount' | 'property_value' | null;

/**
 * Encompass createdBy entity
 */
export interface EncompassCreatedBy {
  entityId: string;
  entityType: string;
}

/**
 * Encompass Itemization Fee
 * Maps to the encompass_itemization_fees table
 */
export interface EncompassItemizationFee {
  id: string;
  encompassFeeId: string;
  name: string;
  sections: LESection[];
  complianceFee: string | null;
  complianceFeeId: string | null;
  ucdFee: string | null;
  encompassCreatedBy: EncompassCreatedBy | null;
  isActive: boolean;
  defaultAmount: number;
  calculationType: FeeCalculationType;
  calculationBase: FeeCalculationBase;
  aprAffects: boolean;
  description: string | null;
  lastSyncedAt: string | null;
  syncSource: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Fee Sync Log entry
 * Maps to the fee_sync_log table
 */
export interface FeeSyncLog {
  id: string;
  syncType: 'full' | 'incremental';
  status: 'started' | 'completed' | 'failed';
  feesAdded: number;
  feesUpdated: number;
  feesRemoved: number;
  errorMessage: string | null;
  triggeredBy: string | null;
  startedAt: string;
  completedAt: string | null;
}

/**
 * Fee sync result from edge function
 */
export interface FeeSyncResult {
  success: boolean;
  syncLogId: string;
  feesAdded: number;
  feesUpdated: number;
  feesRemoved: number;
  totalFees: number;
  duration: number;
  error?: string;
}

/**
 * Fee status response from edge function
 */
export interface FeeStatusResponse {
  totalFees: number;
  activeFees: number;
  lastSync: FeeSyncLog | null;
}

/**
 * Fee history response from edge function
 */
export interface FeeHistoryResponse {
  history: FeeSyncLog[];
}

/**
 * Fees response from edge function
 */
export interface FeesResponse {
  fees: EncompassItemizationFee[];
}

/**
 * Database row format (snake_case)
 * Used for direct Supabase queries
 */
export interface EncompassItemizationFeeRow {
  id: string;
  encompass_fee_id: string;
  name: string;
  sections: string[];
  compliance_fee: string | null;
  compliance_fee_id: string | null;
  ucd_fee: string | null;
  encompass_created_by: EncompassCreatedBy | null;
  is_active: boolean;
  default_amount: number;
  calculation_type: string;
  calculation_base: string | null;
  apr_affects: boolean;
  description: string | null;
  last_synced_at: string | null;
  sync_source: string;
  created_at: string;
  updated_at: string;
}

/**
 * Fee Sync Log row format (snake_case)
 */
export interface FeeSyncLogRow {
  id: string;
  sync_type: string;
  status: string;
  fees_added: number;
  fees_updated: number;
  fees_removed: number;
  error_message: string | null;
  triggered_by: string | null;
  started_at: string;
  completed_at: string | null;
}

/**
 * Transform database row to frontend model
 */
export function transformFeeRow(row: EncompassItemizationFeeRow): EncompassItemizationFee {
  return {
    id: row.id,
    encompassFeeId: row.encompass_fee_id,
    name: row.name,
    sections: row.sections as LESection[],
    complianceFee: row.compliance_fee,
    complianceFeeId: row.compliance_fee_id,
    ucdFee: row.ucd_fee,
    encompassCreatedBy: row.encompass_created_by,
    isActive: row.is_active,
    defaultAmount: row.default_amount,
    calculationType: row.calculation_type as FeeCalculationType,
    calculationBase: row.calculation_base as FeeCalculationBase,
    aprAffects: row.apr_affects,
    description: row.description,
    lastSyncedAt: row.last_synced_at,
    syncSource: row.sync_source,
    createdAt: row.created_at,
    updatedAt: row.updated_at
  };
}

/**
 * Transform sync log row to frontend model
 */
export function transformSyncLogRow(row: FeeSyncLogRow): FeeSyncLog {
  return {
    id: row.id,
    syncType: row.sync_type as 'full' | 'incremental',
    status: row.status as 'started' | 'completed' | 'failed',
    feesAdded: row.fees_added,
    feesUpdated: row.fees_updated,
    feesRemoved: row.fees_removed,
    errorMessage: row.error_message,
    triggeredBy: row.triggered_by,
    startedAt: row.started_at,
    completedAt: row.completed_at
  };
}
